/**
 * Suppliers — pilot 1 of the resource-kit v2 migration (#1800).
 *
 * Authored against the v2 model/view contracts and consumed directly as a
 * `ResourceBundle` by `ResourcePage`. Since #1805 the
 * view side is the FULL contract — `defineListView` + `defineFormView` — and
 * the host side is `ResourceHost`. It remains the simplest resource in the
 * repo: pure static CRUD, no bespoke detail block on the form rail, and none
 * of the four capabilities the parity manifest (§5) parks on the host.
 */
import {
  type EnumOption,
  createFieldConstructors,
  defineFormView,
  defineListView,
  defineModel,
} from '@resource-kit/model';
import type { ResourceBundle } from '@resource-kit/controllers/react';
import type { Supplier } from '@/services';
import { supplierAPI, toListResult } from '@/services';
import { CATALOG_SUPPLIER_FILTERS, CATALOG_SUPPLIER_SORTS } from '@/services/filters.generated';
import { PERMISSIONS } from '@/permissions';

const statusOptions: EnumOption[] = [
  { value: 'active', label: 'supplier.enum.status.active', color: 'success' },
  { value: 'paused', label: 'supplier.enum.status.paused', color: 'secondary' },
];

const f = createFieldConstructors<Supplier>();

// `encodeEmpty: ''` is load-bearing, not decoration: the PATCH schema is
// `supplierCreate.partial()` of `z.string().optional()`
// (`backend/src/contexts/kitchen/http/catalog-suppliers.ts::supplierCreate`,
// and `supplierPatch`, its `.partial()`, just below) — it
// ACCEPTS `''` and REJECTS `null`, so clearing one of these columns with
// `null` would 400 the whole update.
export const supplierModel = defineModel<Supplier>()({
  name: 'supplier',
  idField: 'id',
  recName: 'name',
  fields: {
    id: f.id({ label: 'supplier.field.id' }),
    name: f.text({ label: 'supplier.field.name', encodeEmpty: '' }),
    contact: f.text({ label: 'supplier.field.contact', encodeEmpty: '' }),
    phone: f.phone({ label: 'supplier.field.phone', encodeEmpty: '' }),
    email: f.email({ label: 'supplier.field.email', encodeEmpty: '' }),
    lead_time_days: f.number({ label: 'supplier.field.leadTimeDays' }),
    min_order_value: f.text({ label: 'supplier.field.minOrderValue', encodeEmpty: '' }),
    currency: f.text({ label: 'supplier.field.currency', encodeEmpty: '' }),
    payment_terms: f.text({ label: 'supplier.field.paymentTerms', encodeEmpty: '' }),
    status: f.enum({ label: 'supplier.field.status', options: statusOptions }),
    created_at: f.datetime({ label: 'supplier.field.createdAt' }),
    updated_at: f.datetime({ label: 'supplier.field.updatedAt' }),
  },
});

// The ONE ordered field sequence. It carries the modifiers for BOTH surfaces
// because the native table and record column builders share these items; the
// form view below re-groups those same names, it does not re-declare
// their settings. `capabilities.search` is deliberately absent: #1792 gave this
// route an `x-searchable` column, but suppliers has never offered a search box
// and turning one on is a product decision, not a migration side effect.
export const supplierListView = defineListView(supplierModel, {
  items: [
    { field: 'id', columnInvisible: true, invisible: true },
    { field: 'name' },
    { field: 'contact' },
    { field: 'phone' },
    { field: 'email' },
    { field: 'lead_time_days' },
    { field: 'min_order_value', columnInvisible: true },
    { field: 'currency', columnInvisible: true },
    { field: 'payment_terms', columnInvisible: true },
    { field: 'status' },
    { field: 'created_at', columnInvisible: true, invisible: true },
    { field: 'updated_at', columnInvisible: true, invisible: true },
  ],
  defaultSort: { field: 'created_at', dir: 'desc' },
});

// `kind: 'fields'` tabs render as the form's rail tabs — the #1918 display,
// which this resource declares as the `'tab'` group display rather than `'card'`.
// They stay name-only for the reason above.
// Every EDITABLE field lives in the first tab (#1922): the former
// `commercial` group was a second rail trigger holding four more inputs of the
// same edit. `meta` stays behind because both of its fields are
// `serverAssigned` and therefore read-only on every surface.
export const supplierFormView = defineFormView(supplierModel, {
  groupDisplay: 'tab',
  children: [
    {
      kind: 'fields',
      id: 'identity',
      label: 'supplier.group.identity',
      items: [
        { field: 'name' },
        { field: 'contact' },
        { field: 'phone' },
        { field: 'email' },
        { field: 'status' },
        { field: 'lead_time_days' },
        { field: 'min_order_value' },
        { field: 'currency' },
        { field: 'payment_terms' },
      ],
    },
    {
      kind: 'fields',
      id: 'meta',
      label: 'supplier.group.meta',
      // `id` sits with the other server-assigned stamps rather than in
      // `identity`: it is a read-only UUID, and `identity` is the edit form.
      items: [{ field: 'id' }, { field: 'created_at' }, { field: 'updated_at' }],
    },
  ],
});

export const supplierResource: ResourceBundle<Supplier, typeof supplierModel.fields> = {
  model: supplierModel,
  list: supplierListView,
  form: supplierFormView,
  host: {
    api: {
      // The service already answers the unwrapped detail record; the list
      // routes answer the kernel's `size`-keyed page, so only they are adapted
      // (#2167 PR-2 retired the reverse `ApiResponse` bridge #1151 put here).
      list: async (params) => toListResult(await supplierAPI.list(params)),
      get: (id) => supplierAPI.detail(id),
      create: (body) => supplierAPI.create(body),
      update: (id, body) => supplierAPI.update(id, body),
      delete: (id) => supplierAPI.delete(id),
      // Suppliers is a relation TARGET (#1804 batch C): `ingredients.default_supplier_id`
      // points here. The two reference reads live on the HOST, next to the rest
      // of the resource's API, so `features/relations.ts` registers the target by
      // reading this declaration instead of re-deriving it — one place says how a
      // supplier is fetched.
      //
      // Both are backed by real endpoints, which is the whole test for declaring
      // them: `GET /api/catalog/suppliers/by-ids` (#1793) and the list route's
      // `x-searchable` on `name` (#1792, `CATALOG_SUPPLIER_SEARCH === ['name']`).
      getMany: (ids) => supplierAPI.byIds(ids),
      searchReference: async (query, params) =>
        toListResult(await supplierAPI.list({ ...params, search: query })),
    },
    label: 'supplier.label',
    slug: 'catalog-suppliers',
    listTitle: 'supplier.listTitle',
    listDescription: 'supplier.listDescription',
    // suppliers re-gated onto `catalog:read` — the backend folded the
    // legacy `suppliers:*` keys into the unified `catalog:*` module (#943).
    permission: {
      list: PERMISSIONS.CATALOG_READ,
      read: PERMISSIONS.CATALOG_READ,
      // Mutations require `catalog:write` (backend write routes enforce it);
      // gating the buttons here stops a read-only viewer seeing a control
      // the server would only 403 — the repo's forbidden deceptive-affordance.
      create: PERMISSIONS.CATALOG_WRITE,
      update: PERMISSIONS.CATALOG_WRITE,
      delete: PERMISSIONS.CATALOG_WRITE,
    },
    // EMPTY on purpose (#2114). `/api/catalog/suppliers` declares exactly
    // `search`/`page`/`size` — no filter parameter of any kind — so nothing on
    // this list is filterable, and the generated constant says so.
    //
    // OMITTING this key is NOT the same statement: `undefined` reads as
    // "unrestricted", and `deriveQueryColumnsFromFields` then hands the
    // QueryBuilder every projectable field with the full operator registry.
    // Measured through `ResourcePage` before this line existed: the field menu
    // offered all TWELVE columns, and `toApiListParams` dropped every one of
    // them before the request — with no warning either, since
    // `droppedFilterFields` returns [] when there is no whitelist to check
    // against. `{}` deletes those twelve lying columns and turns the silent
    // drop into a visible, machine-checked one.
    //
    // Bound by reference, never as a `{}` literal, so regeneration flows
    // through (#1837 guardrail A).
    filterable: CATALOG_SUPPLIER_FILTERS,
    // The same seam, one face over (#2114). `/api/catalog/suppliers` declares
    // `x-sortable: []` and its handler never reads a sort param, but an
    // ABSENT declaration made `createColumns` mark every header sortable.
    // Losing those headers is the point: each one issued a sort the route
    // ignores, which on screen is indistinguishable from a sort that ran.
    sortable: CATALOG_SUPPLIER_SORTS,
    searchPlaceholder: 'supplier.searchPlaceholder',
    formConfig: {
      createTitle: 'supplier.form.createTitle',
      editTitle: 'supplier.form.editTitle',
      columns: 2,
      maxWidth: 'md',
    },
  },
};
